import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { getActiveOverviewTab, setActiveOverviewTab } from 'redux/ModelsSlice';

import { TabType } from '../types';
import { DEFAULT_MODEL_TAB_URL, getTabFromUrlValue, getUrlValueFromTab } from '../constants';

const useActiveTab = (initialTab: TabType = 'dashboard') => {
  const dispatch: any = useDispatch();
  const storedTab = useSelector(getActiveOverviewTab) as TabType | undefined;
  const navigate = useNavigate();
  const { modelId, tab } = useParams();
  const tabFromUrl = getTabFromUrlValue(tab ?? null);
  const activeTab = tabFromUrl ?? storedTab ?? initialTab;

  useEffect(() => {
    if (tabFromUrl) {
      if (storedTab !== tabFromUrl) {
        dispatch(setActiveOverviewTab(tabFromUrl));
      }
      return;
    }

    const fallbackTab = storedTab ?? initialTab;
    const fallbackUrlValue = getUrlValueFromTab(fallbackTab);
    if (modelId) {
      navigate(`/model/${modelId}/${fallbackUrlValue || DEFAULT_MODEL_TAB_URL}`, { replace: true });
    }

    if (!storedTab) {
      dispatch(setActiveOverviewTab(fallbackTab));
    }
  }, [dispatch, initialTab, modelId, navigate, storedTab, tabFromUrl]);

  const handleTabChange = useCallback(
    (nextTab: TabType) => {
      dispatch(setActiveOverviewTab(nextTab));
      const nextUrlValue = getUrlValueFromTab(nextTab);
      if (modelId) {
        navigate(`/model/${modelId}/${nextUrlValue}`, { replace: false });
      }
    },
    [dispatch, modelId, navigate],
  );

  return {
    activeTab,
    setActiveTab: handleTabChange,
  };
};

export default useActiveTab;
